import React, { useEffect, useRef } from 'react';
import { HelpCircle, CheckCircle2, AlertTriangle, X, ArrowRight, CornerDownLeft, ShieldCheck } from 'lucide-react';
import Modal from './Modal';
import { MissingField } from '../../lib/applicationSchema';

interface HelpInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  explanation: string;
  isSensitivePii?: boolean;
}

export function HelpInfoModal({
  isOpen,
  onClose,
  title,
  explanation,
  isSensitivePii = false,
}: HelpInfoModalProps) {
  if (!isOpen) return null;

  return (
    <Modal onClose={onClose} ariaLabel={`Help information for ${title}`}>
      <div className="bg-white rounded-3xl p-5 sm:p-6 max-w-md w-full shadow-2xl space-y-4 border border-slate-100">
        <div className="flex items-start justify-between gap-3 border-b border-slate-100 pb-3">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-2xl bg-blue-50 border border-blue-100 flex items-center justify-center shrink-0">
              <HelpCircle className="h-5 w-5 text-brand" />
            </div>
            <div>
              <span className="text-[10px] font-extrabold uppercase tracking-widest text-brand">Why We Ask</span>
              <h3 className="text-base font-bold text-slate-900 leading-snug">{title}</h3>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="applicant-touch-target p-2 rounded-xl text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-all cursor-pointer"
            aria-label="Close help modal"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <p className="text-sm text-slate-600 leading-relaxed">{explanation}</p>

        {isSensitivePii && (
          <div className="bg-emerald-50/80 border border-emerald-200/80 rounded-2xl p-3.5 flex items-start gap-2.5 text-xs text-emerald-800">
            <ShieldCheck className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />
            <p className="leading-normal">
              <b>Encrypted & Secure:</b> This sensitive record is protected with military-grade <b>AES-256-GCM encryption at rest</b> and accessed solely for visa and sponsorship verification.
            </p>
          </div>
        )}

        <div className="pt-2">
          <button
            type="button"
            onClick={onClose}
            className="applicant-touch-target w-full py-3 bg-slate-100 hover:bg-slate-200 text-slate-800 text-xs font-bold rounded-2xl transition-all cursor-pointer"
          >
            Got it, thanks
          </button>
        </div>
      </div>
    </Modal>
  );
}

interface StepEncouragementModalProps {
  isOpen: boolean;
  onProceed: () => void;
  title: string;
  message: string;
  nextStepTitle?: string;
}

export function StepEncouragementModal({
  isOpen,
  onProceed,
  title,
  message,
  nextStepTitle,
}: StepEncouragementModalProps) {
  const primaryBtnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => primaryBtnRef.current?.focus(), 50);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <Modal onClose={onProceed} ariaLabel="Step completion confirmation">
      <div className="bg-white rounded-3xl p-6 max-w-md w-full shadow-2xl space-y-5 border border-slate-100 text-center">
        <div className="h-14 w-14 rounded-3xl bg-gradient-to-tr from-emerald-500 to-teal-400 text-white flex items-center justify-center mx-auto shadow-lg shadow-emerald-500/20">
          <CheckCircle2 className="h-8 w-8" />
        </div>

        <div className="space-y-1.5">
          <span className="text-[10px] font-extrabold uppercase tracking-widest text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded-full border border-emerald-100">
            Progress Saved
          </span>
          <h3 className="text-lg font-black text-slate-900 mt-2">{title}</h3>
          <p className="text-sm text-slate-600 leading-relaxed px-2">{message}</p>
        </div>

        {nextStepTitle && (
          <div className="bg-slate-50 border border-slate-200/80 rounded-2xl p-3 flex items-center justify-between text-xs text-slate-600">
            <span className="font-semibold text-slate-400 uppercase tracking-wider text-[10px]">Up Next</span>
            <span className="font-bold text-slate-800">{nextStepTitle}</span>
          </div>
        )}

        <button
          ref={primaryBtnRef}
          type="button"
          onClick={onProceed}
          className="applicant-touch-target w-full py-3.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white text-sm font-bold rounded-2xl transition-all shadow-lg shadow-blue-500/20 cursor-pointer active:scale-[0.98] flex items-center justify-center gap-2"
        >
          <span>I'm Ready</span>
          <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </Modal>
  );
}

interface MissingFieldsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onProceedAnyway: () => void;
  missingFields: MissingField[];
}

export function MissingFieldsModal({
  isOpen,
  onClose,
  onProceedAnyway,
  missingFields,
}: MissingFieldsModalProps) {
  const fixBtnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => fixBtnRef.current?.focus(), 50);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <Modal onClose={onClose} ariaLabel="Missing required fields checkpoint">
      <div className="bg-white rounded-3xl p-5 sm:p-6 max-w-md w-full shadow-2xl space-y-4 border border-amber-200/80">
        <div className="flex items-start gap-3 bg-amber-50 border border-amber-200/60 rounded-2xl p-4">
          <div className="h-10 w-10 rounded-2xl bg-amber-100 flex items-center justify-center shrink-0">
            <AlertTriangle className="h-5 w-5 text-amber-700" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-amber-900 leading-snug">Incomplete Step Checkpoint</h3>
            <p className="text-xs text-amber-700 mt-0.5 leading-relaxed">
              Some required fields for this section are missing or incomplete. You can go back to complete them or proceed with a draft status.
            </p>
          </div>
        </div>

        <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
          <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Missing Required Fields:</p>
          <ul className="space-y-1.5">
            {missingFields.map((field, idx) => (
              <li key={idx} className="flex items-center gap-2 text-xs text-slate-700 bg-slate-50 border border-slate-200/70 rounded-xl px-3 py-2">
                <span className="h-1.5 w-1.5 rounded-full bg-amber-500 shrink-0" />
                <span className="font-semibold">{field.label}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="pt-2 space-y-2">
          <button
            ref={fixBtnRef}
            type="button"
            onClick={onClose}
            className="applicant-touch-target w-full py-3.5 bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold rounded-2xl transition-all shadow-md shadow-amber-500/20 cursor-pointer active:scale-[0.98] flex items-center justify-center gap-2"
          >
            <CornerDownLeft className="h-4 w-4" />
            <span>Go Back & Fix</span>
          </button>

          <button
            type="button"
            onClick={onProceedAnyway}
            className="applicant-touch-target w-full py-2.5 bg-transparent hover:bg-slate-50 text-slate-500 hover:text-slate-700 text-xs font-semibold rounded-2xl transition-all cursor-pointer underline text-center"
          >
            Still Proceed (Mark Incomplete)
          </button>
        </div>
      </div>
    </Modal>
  );
}

interface HelpIconButtonProps {
  onClick: (e: React.MouseEvent) => void;
  title?: string;
}

export function HelpIconButton({ onClick, title = 'Why do we ask?' }: HelpIconButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className="applicant-touch-target inline-flex items-center justify-center h-6 w-6 rounded-full bg-slate-100 hover:bg-blue-100 text-slate-400 hover:text-brand transition-all cursor-pointer shrink-0 ml-1.5 active:scale-95"
      aria-label={title}
    >
      <HelpCircle className="h-3.5 w-3.5" />
    </button>
  );
}
